import { PortfolioProjectAdminActions_portfolioProject$key } from '#app/__generated__/relay/PortfolioProjectAdminActions_portfolioProject.graphql.js';
import { PortfolioProjectAdminActionsMutation } from '#app/__generated__/relay/PortfolioProjectAdminActionsMutation.graphql.js';
import { Checkbox } from '#app/components/Controls/src/Checkbox.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useContraMutation } from '#app/hooks/useContraMutation.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { CloseIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { captureException } from '@contra/sentry/react';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';

const RANK_BOOST_MULTIPLIER = 50;

const CloseButton = styled.button`
  position: absolute;
  top: -32px;
  right: 6px;
`;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    position: fixed;
    bottom: 88px;
    right: 16px;
    background-color: white;
    border-radius: 8px;
    box-shadow: ${theme.boxShadow.toast};
    z-index: ${theme.zIndices.tooltip};
  `}
`;

type PortfolioProjectAdminActionsProps = {
  readonly portfolioProjectRef: PortfolioProjectAdminActions_portfolioProject$key;
};

const PortfolioProjectAdminActions = ({
  portfolioProjectRef,
}: PortfolioProjectAdminActionsProps) => {
  const toast = useToast();
  const { id: portfolioProjectId, searchRankBoost } =
    useFragment<PortfolioProjectAdminActions_portfolioProject$key>(
      graphql`
        fragment PortfolioProjectAdminActions_portfolioProject on PortfolioProject {
          id
          searchRankBoost
        }
      `,
      portfolioProjectRef,
    );
  const [updatePortfolioProject] =
    useContraMutation<PortfolioProjectAdminActionsMutation>(graphql`
      mutation PortfolioProjectAdminActionsMutation(
        $input: AdminUpdatePortfolioProjectInput!
      ) {
        adminUpdatePortfolioProject(input: $input) {
          errors {
            __typename
            message
          }
          portfolioProject {
            id
            searchRankBoost
          }
        }
      }
    `);
  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updatePortfolioProject({
        onCompleted: ({ hasErrors, payloadErrors }) => {
          if (hasErrors) {
            toast.error('Error occurred while updating productized service');
          }

          if (payloadErrors) {
            captureException(payloadErrors[0]);
          }
        },
        onError: (error) => {
          toast.error('Error occurred while updating productized service');
          captureException(error);
        },
        variables: {
          input: {
            portfolioProjectId,
            searchRankBoost: event.target.checked ? RANK_BOOST_MULTIPLIER : 0,
          },
        },
      });
    },
    [portfolioProjectId, toast, updatePortfolioProject],
  );

  return (
    <Checkbox
      checked={Boolean(searchRankBoost)}
      onChange={onChange}
    >
      {'\u00A0\u00A0'}
      Boost search rank 🚀
    </Checkbox>
  );
};

export const PortfolioProjectAdminActionsButton = ({
  portfolioProjectRef,
}: PortfolioProjectAdminActionsProps) => {
  const currentUser = useCurrentUser();
  const [isOpen, setIsOpen] = useState(false);

  if (!currentUser?.userFeatures.includes('RATE_USERS')) {
    return null;
  }

  return (
    <Container>
      {isOpen ? (
        <>
          <CloseButton
            onClick={() => setIsOpen(false)}
            type="button"
          >
            <CloseIcon />
          </CloseButton>

          <PortfolioProjectAdminActions
            portfolioProjectRef={portfolioProjectRef}
          />
        </>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          type="button"
        >
          Show
        </button>
      )}
    </Container>
  );
};
